<template>
  <div class="panel-screen">
    <panel-toolbar :period="period" show-period @update:period="changePeriod" />

    <div class="panel-screen__summary">
      <div class="panel-screen__summary-cell">
        <div class="panel-screen__summary-label">{{ $t('influencer_panel.active_campaigns') }}</div>
        <div class="panel-screen__summary-value">{{ activeCount }}</div>
      </div>
      <div class="panel-screen__summary-cell">
        <div class="panel-screen__summary-label">{{ $t('influencer_panel.sales') }}</div>
        <div class="panel-screen__summary-value">{{ totalSales }}</div>
      </div>
      <div class="panel-screen__summary-cell">
        <div class="panel-screen__summary-label">{{ $t('influencer_panel.commission') }}</div>
        <div class="panel-screen__summary-value">
          <hb-money :cents="totalCommissionCents" tone="brand" />
        </div>
      </div>
      <div class="panel-screen__summary-cell">
        <div class="panel-screen__summary-label">{{ $t('influencer_panel.ending_count') }}</div>
        <div class="panel-screen__summary-value">{{ endingCount }}</div>
      </div>
    </div>

    <hb-tabs :value="tab" :tabs="tabs" @input="changeTab" />

    <!-- Minhas campanhas -->
    <template v-if="tab === 'minhas'">
      <b-skeleton-wrapper :loading="loadingMine">
        <template #loading>
          <hb-section><b-skeleton-img no-aspect height="160px" /></hb-section>
        </template>

        <section-error v-if="mineError" :message="mineError" @retry="loadMine" />

        <hb-section v-else-if="!joined.length" :title="$t('influencer_panel.tab_mine')">
          <hb-empty-state :title="$t('influencer_panel.no_campaigns')" icon="TrendingUpIcon" />
        </hb-section>

        <div v-else class="panel-screen__cards">
          <campaign-card
            v-for="item in joined"
            :key="item.id"
            :participation="item"
            @generate-link="openGenerateLink"
            @material="openMaterial"
            @info="openInfo"
            @leave="confirmLeave"
          />
        </div>
      </b-skeleton-wrapper>
    </template>

    <!-- Disponíveis -->
    <template v-else-if="tab === 'disponiveis'">
      <b-skeleton-wrapper :loading="loadingAvailable">
        <template #loading>
          <hb-section><b-skeleton-img no-aspect height="160px" /></hb-section>
        </template>

        <section-error v-if="availableError" :message="availableError" @retry="loadAvailable" />
        <available-campaigns-pane v-else :campaigns="available" @join="join" />
      </b-skeleton-wrapper>
    </template>

    <!-- Meus links -->
    <template v-else>
      <b-skeleton-wrapper :loading="loadingLinks">
        <template #loading>
          <hb-section><b-skeleton-img no-aspect height="160px" /></hb-section>
        </template>

        <section-error v-if="linksError" :message="linksError" @retry="loadLinks" />
        <my-links-pane v-else :links="links" @generate-link="openGenerateLink()" />
      </b-skeleton-wrapper>
    </template>

    <generate-link-modal
      ref="generateLink"
      :participations="joined"
      :preselected="linkTarget"
      @created="onLinkCreated"
    />

    <campaign-material-modal ref="material" :participation="materialTarget" />
    <campaign-info-modal ref="info" />
  </div>
</template>

<script>
import { BSkeletonImg, BSkeletonWrapper } from 'bootstrap-vue'
import InfluencerPanelService from '@/services/api/InfluencerPanelService'
import HbSection from '@/components/hubee/HbSection.vue'
import HbTabs from '@/components/hubee/HbTabs.vue'
import HbMoney from '@/components/hubee/HbMoney.vue'
import HbEmptyState from '@/components/hubee/HbEmptyState.vue'
import PanelToolbar from './components/PanelToolbar.vue'
import CampaignCard from './components/CampaignCard.vue'
import AvailableCampaignsPane from './components/AvailableCampaignsPane.vue'
import MyLinksPane from './components/MyLinksPane.vue'
import GenerateLinkModal from './components/GenerateLinkModal.vue'
import CampaignMaterialModal from './components/CampaignMaterialModal.vue'
import CampaignInfoModal from './components/CampaignInfoModal.vue'
import SectionError from './components/SectionError.vue'
import { DEFAULT_PERIOD, PERIODS, periodParams, extractMessage } from './panel'
import { participationMetric, campaignName } from './campaign'

const TABS = ['minhas', 'disponiveis', 'links']

/**
 * Minhas Campanhas.
 *
 * A aba fica na URL (`?tab=`) para que recarregar a pagina — ou mandar o link para
 * alguem — caia de volta no mesmo lugar. Cada aba carrega o seu recurso quando e aberta
 * pela primeira vez, em vez de as tres buscarem juntas na montagem.
 */
export default {
  components: {
    BSkeletonImg,
    BSkeletonWrapper,
    HbSection,
    HbTabs,
    HbMoney,
    HbEmptyState,
    PanelToolbar,
    CampaignCard,
    AvailableCampaignsPane,
    MyLinksPane,
    GenerateLinkModal,
    CampaignMaterialModal,
    CampaignInfoModal,
    SectionError,
  },
  data: () => ({
    service: null,
    tab: 'minhas',
    period: DEFAULT_PERIOD,
    participations: [],
    available: [],
    links: [],
    loadingMine: true,
    loadingAvailable: false,
    loadingLinks: false,
    loadedAvailable: false,
    loadedLinks: false,
    mineError: '',
    availableError: '',
    linksError: '',
    linkTarget: null,
    materialTarget: null,
  }),
  computed: {
    tabs() {
      return [
        { key: 'minhas', label: this.$t('influencer_panel.tab_mine'), count: this.joined.length },
        { key: 'disponiveis', label: this.$t('influencer_panel.tab_available') },
        { key: 'links', label: this.$t('influencer_panel.tab_links') },
      ]
    },
    joined() {
      return this.participations.filter(p => !['LE', 'RJ'].includes(p.status))
    },
    activeCount() {
      return this.participations.filter(p => p.status === 'AC').length
    },
    endingCount() {
      return this.participations.filter(
        p => p.status === 'AC' && p.days_to_end !== null && p.days_to_end >= 0 && p.days_to_end <= 7
      ).length
    },
    totalSales() {
      return this.joined.reduce((sum, p) => sum + participationMetric(p, 'conversions'), 0)
    },
    totalCommissionCents() {
      return this.joined.reduce((sum, p) => sum + participationMetric(p, 'commission_cents'), 0)
    },
  },
  beforeMount() {
    this.service = InfluencerPanelService.build(this.$vs)

    const { tab, period } = this.$route.query
    if (TABS.includes(tab)) this.tab = tab
    if (PERIODS.includes(period)) this.period = period
  },
  mounted() {
    this.loadMine()
    this.ensureTabLoaded()
  },
  methods: {
    loadMine() {
      this.loadingMine = true
      this.mineError = ''

      return this.service.myCampaigns(periodParams(this.period))
        .then(list => { this.participations = list || [] })
        .catch(error => { this.mineError = extractMessage(error) })
        .finally(() => { this.loadingMine = false })
    },
    loadAvailable() {
      this.loadingAvailable = true
      this.availableError = ''
      this.loadedAvailable = true

      return this.service.availableCampaigns()
        .then(list => { this.available = list || [] })
        .catch(error => { this.availableError = extractMessage(error) })
        .finally(() => { this.loadingAvailable = false })
    },
    loadLinks() {
      this.loadingLinks = true
      this.linksError = ''
      this.loadedLinks = true

      return this.service.links(periodParams(this.period))
        .then(list => { this.links = list || [] })
        .catch(error => { this.linksError = extractMessage(error) })
        .finally(() => { this.loadingLinks = false })
    },
    ensureTabLoaded() {
      if (this.tab === 'disponiveis' && !this.loadedAvailable) this.loadAvailable()
      if (this.tab === 'links' && !this.loadedLinks) this.loadLinks()
    },
    changeTab(tab) {
      this.tab = tab
      // `replace` para nao encher o historico, e o catch porque o vue-router 3.4
      // rejeita navegacao duplicada como promise nao tratada.
      this.$router.replace({ query: { ...this.$route.query, tab } }).catch(() => {})
      this.ensureTabLoaded()
    },
    changePeriod(period) {
      this.period = period
      this.$router.replace({ query: { ...this.$route.query, period } }).catch(() => {})
      this.loadMine()
      if (this.loadedLinks) this.loadLinks()
    },
    join(campaign) {
      this.$vs.loading()
      this.service.join(campaign.id)
        .then(() => {
          this.notifySuccess(this.$vs, this.$t('influencer_panel.join'))
          this.loadMine()
          this.loadAvailable()
        })
        .catch(error => { this.$vs.notify({ text: extractMessage(error), color: 'danger' }) })
        .finally(() => { this.$vs.loading.close() })
    },
    confirmLeave(participation) {
      this.$bvModal.msgBoxConfirm(
        this.$t('influencer_panel.leave_confirm', { campaign: campaignName(participation) }),
        {
          title: this.$t('influencer_panel.leave'),
          okVariant: 'danger',
          okTitle: this.$t('influencer_panel.leave'),
          cancelTitle: this.$t('action.cancel'),
          cancelVariant: 'outline-secondary',
          centered: true,
        }
      ).then(confirmed => {
        if (confirmed) this.leave(participation)
      })
    },
    leave(participation) {
      this.$vs.loading()
      this.service.leave(participation.id)
        .then(() => {
          this.notifySuccess(this.$vs, this.$t('influencer_panel.leave'))
          this.loadMine()
        })
        .catch(error => { this.$vs.notify({ text: extractMessage(error), color: 'danger' }) })
        .finally(() => { this.$vs.loading.close() })
    },
    openGenerateLink(participation = null) {
      this.linkTarget = participation ? participation.id : null
      this.$refs.generateLink.open(this.linkTarget)
    },
    onLinkCreated() {
      if (this.loadedLinks) this.loadLinks()
    },
    openMaterial(participation) {
      this.materialTarget = participation
      this.$refs.material.open(participation)
    },
    openInfo(participation) {
      this.$refs.info.open(participation)
    },
  },
}
</script>

<style scoped>
.panel-screen {
  display: flex;
  flex-direction: column;
  gap: clamp(1rem, 1.8vw, 1.5rem);
}

.panel-screen__summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1px;
  background: var(--border-color);
  border-radius: var(--radius-card);
  overflow: hidden;
  box-shadow: var(--shadow-card);
}

.panel-screen__summary-cell {
  background: var(--surface-card);
  padding: 1.1rem 1.25rem;
}

.panel-screen__summary-label {
  font-size: var(--fs-sm);
  color: var(--text-muted);
}

.panel-screen__summary-value {
  margin-top: 0.3rem;
  font-size: var(--fs-h2);
  font-weight: var(--fw-medium);
  color: var(--text-heading);
  line-height: 1.2;
}

.panel-screen__cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: clamp(1rem, 1.8vw, 1.5rem);
}
</style>
